import React, {
	useMemo,
	useState,
} from 'react';
import Title from 'antd/lib/typography/Title';
import { PlusOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { sortByDates } from '@shared/utils/sortByDates';
import { NumberFormatStyle, FixtureTypeLabels, HireInvoiceItemStates } from '@shared/utils/constants';
import type { TransformedExpenses } from '@api/utils/getTransformedVoyageExpenses';
import type { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import type { GetDisbursementAccountsResponse } from '@api/features/disbursement-accounts/getDisbursementAccounts';
import { formatNumberStyle } from '@api/features/voyages/helpers/generatePnlPdf';
import type VoyageExpenseReceivable from '@api/models/voyage-expense-receivable';
import CreateExpenseForm from '@client/components/CreateExpenseForm/CreateExpenseForm';
import Table, { type TableProps } from '@client/components/Table/Table';
import Button from '@client/components/Button';
import { calculateTotalPayment, getExpenseStatus } from '@client/components/VendorExpensePaymentStateTag';
import useNumberFormatStyle from '@client/utils/hooks/useExportNotation';
import styles from '../ExpensesScreen/components/styles/ExpenseScreen.module.css';
import useGetVendorExpensesTableColumns from './getVendorExpensesTableColumn';

type VendorExpenseRow = GetVoyageExpensesResponse['rows'][number];
type DisbursementAccountRow = GetDisbursementAccountsResponse[number];

type ExpenseTableRow = (VendorExpenseRow | DisbursementAccountRow) & {
	itemDescription?: string;
	supplierName?: string | null;
	vesselName?: string | null;
	voyageIdentifier?: string | null;
	fixtureType?: VendorExpenseRow['fixtureType'] | null;
	fixtureCurrency?: string | null;
	VoyageExpensePayments?: Array<{ amount: number }>;
	payments?: Array<{ amount: number }>;
	receivable?: (VoyageExpenseReceivable & { HIIVoyageExpense?: unknown | null }) | null;
	receivableStatus?: string;
	paymentStatus?: ReturnType<typeof getExpenseStatus>;
};

type Props = {
	expenses: ExpenseTableRow[];
	item: TransformedExpenses | ExpenseTableRow | undefined;
	handleClick: (id: number, itemType: 'vendorExpense' | 'portDA') => void;
	refreshExpenses: () => void;
	isLoading: boolean;
	pagination: TablePaginationConfig;
	onSearch: (q: string) => void;
	onTableChange: (
		newPagination: TablePaginationConfig,
		newFilters: Record<string, FilterValue | null>,
		newSorter: SorterResult<ExpenseTableRow> | SorterResult<ExpenseTableRow>[],
	) => void;
	count: number;
	setHideCompleted: (b: boolean) => void;
	hideCompleted: boolean;
};

const getPayments = (expense: ExpenseTableRow): Array<{ amount: number }> => {
	if (Array.isArray(expense.VoyageExpensePayments)) {
		return expense.VoyageExpensePayments;
	}

	if (Array.isArray(expense.payments)) {
		return expense.payments;
	}

	return [];
};

const isDisbursementAccount = (expense: ExpenseTableRow): expense is ExpenseTableRow & DisbursementAccountRow => (
	'costItems' in expense
);

const VendorExpensesTable = ({
	expenses,
	item,
	handleClick,
	refreshExpenses,
	isLoading,
	pagination,
	onSearch,
	onTableChange,
	count,
	setHideCompleted,
	hideCompleted,
}: Props) => {
	const [drawerVisible, setDrawerVisible] = useState(false);
	const { formatStyle } = useNumberFormatStyle(NumberFormatStyle.UkUs);

	const sortedExpenses = useMemo<ExpenseTableRow[]>(() => {
		if (expenses === undefined) {
			return [];
		}

		const withPaymentStatus = expenses.map((e) => {
			const totalPayment = calculateTotalPayment(getPayments(e));
			const paymentStatus = getExpenseStatus(totalPayment, Number(e.amount ?? 0), e.dueDate);

			return {
				...e,
				paymentStatus,
			};
		});

		return sortByDates(withPaymentStatus, 'createdAt', { newestFirst: true }) as ExpenseTableRow[];
	}, [expenses]);

	const filterMaps = useMemo(() => {
		const voyages = new Set<string>();

		expenses?.forEach((item) => {
			if (item.voyageIdentifier != null) {
				voyages.add(item.voyageIdentifier);
			}
		});

		return {
			voyages: Array.from(voyages),
		};
	}, [expenses]);

	const columns = useGetVendorExpensesTableColumns(filterMaps) as TableProps<ExpenseTableRow>['columns'];

	const csvData = useMemo(() => {
		const baseRows: any[] = [];

		sortedExpenses.forEach((dataItem) => {
			if (dataItem == null) {
				return;
			}

			const capitalizeFirstLetter = (s?: string | null) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

			const getApprovalLabel = (state?: string | null) => state ? capitalizeFirstLetter(state) : 'Not Set';

			const getReceivableStatus = (receivable: ExpenseTableRow['receivable']) => {
				if (receivable == null) {
					return 'Not Created';
				}

				if (receivable.HIIVoyageExpense != null) {
					return capitalizeFirstLetter(HireInvoiceItemStates.INVOICED);
				}

				return capitalizeFirstLetter(HireInvoiceItemStates.PENDING);
			};

			const localAmountNum = Number(dataItem.amount ?? 0);
			const exchangeRate = Number(dataItem.exchangeRate ?? 1) || 1;
			const contractAmountNum = localAmountNum / exchangeRate;
			const totalPaidNum = calculateTotalPayment(getPayments(dataItem));
			const outstandingNum = localAmountNum - totalPaidNum;
			const expenseStatus = getExpenseStatus(totalPaidNum, localAmountNum, dataItem.dueDate);
			const rebillableState = getReceivableStatus(dataItem.receivable);
			const fixtureTypeLabel = dataItem.fixtureType == null ? '' : FixtureTypeLabels[dataItem.fixtureType];

			const localAmount = formatNumberStyle(localAmountNum, formatStyle);
			const contractAmount = formatNumberStyle(contractAmountNum, formatStyle);
			const totalPaid = formatNumberStyle(totalPaidNum, formatStyle);
			const outstanding = formatNumberStyle(outstandingNum, formatStyle);

			baseRows.push([
				dataItem.itemDescription,
				dataItem.supplierName,
				dataItem.vesselName,
				dataItem.voyageIdentifier,
				fixtureTypeLabel,
				dataItem.customInvoiceId,
				dataItem.invoiceDate,
				dataItem.dueDate,
				dataItem.fixtureCurrency ?? dataItem.currency,
				contractAmount,
				dataItem.currency,
				localAmount,
				totalPaid,
				outstanding,
				rebillableState,
				capitalizeFirstLetter(expenseStatus),
				getApprovalLabel(dataItem.state),
			]);
		});

		return [
			[
				'Description',
				'Supplier',
				'Vessel',
				'Contract',
				'Contract Type',
				'Id',
				'Invoice Date',
				'Due Date',
				'Contract Currency',
				'Contract Amount',
				'Local Currency',
				'Local Amount',
				'Paid',
				'Outstanding',
				'Rebillable',
				'Payment',
				'Approval',
			],
			...baseRows,
		];
	}, [sortedExpenses, formatStyle]);

	return (
		<>
			<div className={styles.tableHeaderSection}>
				<Title level={3}>
					Vendor Expenses
				</Title>
			</div>
			<Table<ExpenseTableRow>
				loading={isLoading}
				scroll={{ x: 800 }}
				columns={columns}
				dataSource={sortedExpenses}
				size="small"
				onRowClick={(rowItem) => {
					// Check to see whether item is vendor expense or DA
					if (isDisbursementAccount(rowItem)) {
						handleClick(rowItem.id, 'portDA');

						return;
					}

					handleClick(rowItem.id, 'vendorExpense');
				}}
				showSearch
				pagination={{
					...pagination,
					total: count ?? 0,
					pageSizeOptions: [50, 100, 200, 500],
				}}
				searchExtra={(
					<Checkbox
						checked={hideCompleted}
						onChange={(e) => setHideCompleted(e.target.checked)}
						style={{ width: 300 }}
					>
						Hide completed contracts
					</Checkbox>
				)}
				onChange={onTableChange}
				onSearch={onSearch}
				showExportToCsv
				exportData={csvData}
				rowClassName={(record) => (record === item ? styles.selectedRow : '')}
				extraArea={(
					<Button
						style={{ marginLeft: 10 }}
						key="createExpense"
						onClick={() => setDrawerVisible(true)}
						type="primary"
						icon={(
							<PlusOutlined />
						)}
					>
						Create Expense
					</Button>
				)}
			/>
			<CreateExpenseForm
				open={drawerVisible}
				onClose={() => {
					setDrawerVisible(false);
					refreshExpenses();
				}}
				refreshDetails={() => (
					refreshExpenses()
				)}
			/>
		</>
	);
};

export default VendorExpensesTable;
